import React from 'react';
import { View, Image, Linking } from 'react-native';
import { Text } from 'react-native-paper';
import styled from '@emotion/native';
import { H2, BodyText } from '@/shared/components/StyledText';
import { green, sand } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import type { EventConfig } from './constants';
import { FONT_STYLES } from '@/shared/styles/constants';

interface NormalShowSectionProps {
  config: EventConfig['normalShow'];
}

const NormalShowSection: React.FC<NormalShowSectionProps> = ({ config }) => {
  const { isDesktop } = useBreakpoint();

  const handleTicketsClick = async () => {
    try {
      await Linking.openURL(config.eventbriteUrl);
    } catch (error) {
      console.error('Error opening Eventbrite URL:', error);
    }
  };

  return (
    <NormalShowSectionContainer isDesktop={isDesktop}>
      <NormalShowContent isDesktop={isDesktop}>
        <NormalShowText isDesktop={isDesktop}>
          <NormalShowTitle isDesktop={isDesktop}>
            <NormalShowBoldTitle>Not-A-Normal Show</NormalShowBoldTitle> is a
            two-weekend shopping, community, and cultural event by{' '}
            <NormalShowBoldTitle>Normal New York</NormalShowBoldTitle>
          </NormalShowTitle>
          <NormalShowDescription isDesktop={isDesktop}>
            {config.description}
          </NormalShowDescription>
          <TicketsButton isDesktop={isDesktop} onPress={handleTicketsClick}>
            <TicketsButtonText>GET TICKETS</TicketsButtonText>
            <LinkOutIcon
              source={require('@/assets/images/event-landing/LinkOut.png')}
            />
          </TicketsButton>
        </NormalShowText>
        <NormalShowImage
          isDesktop={isDesktop}
          source={require('@/assets/images/event-landing/NormalImg.png')}
        />
      </NormalShowContent>
    </NormalShowSectionContainer>
  );
};

export default NormalShowSection;

const NormalShowSectionContainer = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    backgroundColor: sand,
    paddingHorizontal: isDesktop ? 80 : 32,
    paddingVertical: isDesktop ? 80 : 60,
    minHeight: isDesktop ? 336 : 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const NormalShowContent = styled(View)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
    alignItems: 'center',
    gap: isDesktop ? 80 : 24,
    maxWidth: 1200,
    width: '100%',
  }),
);

const NormalShowText = styled(View)<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    textAlign: isDesktop ? 'left' : 'center',
    alignItems: isDesktop ? 'flex-start' : 'center',
  }),
);

const NormalShowBoldTitle = styled(H2)({
  fontFamily: FONT_STYLES.helvetica.fontFamily,
  fontSize: 30,
  fontWeight: 'bold',
  color: green,
});

const NormalShowTitle = styled(H2)<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    fontFamily: FONT_STYLES.helvetica.fontFamily,
    fontSize: 30,
    fontWeight: '300',
    color: green,
    textAlign: isDesktop ? 'left' : 'center',
  }),
);

const NormalShowDescription = styled(BodyText)<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    fontSize: 16,
    fontWeight: 'normal',
    color: green,
    maxWidth: 500,
    textAlign: isDesktop ? 'left' : 'center',
  }),
);

const TicketsButton = styled.TouchableOpacity<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: green,
    width: 200,
    height: 56,
    borderRadius: 28,
    alignSelf: isDesktop ? 'flex-start' : 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  }),
);

const TicketsButtonText = styled(Text)({
  color: sand,
  fontSize: 12,
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: 0.05,
  fontFamily: 'Lora',
});

const LinkOutIcon = styled(Image)({
  width: 16,
  height: 16,
});

const NormalShowImage = styled(Image)<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    height: isDesktop ? 336 : 220,
    width: isDesktop ? 447 : 294,
    maxWidth: isDesktop ? 447 : 294,
    borderRadius: 8,
  }),
);
